import { ReportingRouterConfig, ReportingRouterInit } from './reportingRouterConfig';
import { ReportingRouter, ReportingRouterEnhancer } from './reportingRouterInterface';
import {
    ReportingContainerBaseConfig,
    ReportingContextBaseConfig,
    ReportingControllerBaseConfig,
    ReportingLoaderBaseConfig,
    ReportingRouterInfra,
    ReportingViewBaseConfig,
} from './base';
import {
    createReportChapterView,
    createReportDetailView,
    createReportGenerateContainer,
    createReportGenerateController,
    createReportGenerateLoader,
    createReportGenerateView,
    createReportDetailContainer,
    createReportDetailController,
    createReportDetailLoader,
    createReportNewContainer,
    createReportNewController,
    createReportNewLoader,
    createReportNewView,
    createReportOverviewContainer,
    createReportOverviewController,
    createReportOverviewLoader,
    createReportOverviewView,
    createReportChapterContainer,
    createReportChapterLoader,
    createReportChapterController,
    createContentOverlayContainer,
    createContentOverlayController,
    createContentOverlayLoader,
    createContentOverlayView,
    createContentDetailContainer,
    createContentDetailLoader,
    createDocumentDetailContainer,
    createDocumentDetailLoader,
    createDocumentDetailController,
    createDocumentDetailView,
    createRecordOverlayContainer,
    createRecordOverlayLoader,
    createRecordOverlayController,
    createRecordOverlayView,
    createTaskListView,
    createTaskListLoader,
    createTaskListController,
    createTaskListContainer,
    createTaskItemController,
    createTaskItemLoader,
    createTaskItemContainer,
    createHomeOptionContainer,
    createHomeOptionController,
} from './page';
import {
    createReportSectionItemController,
    createSectionItemContainer,
    createSourceListContainer,
    createSourceListState,
    createSourceListLoader,
    createSourceListController,
    createToolbarItemController,
    createToolbarItemContainer,
    createButtonBlockContainer,
    createButtonBlockController,
    createMentionSpanContainer,
    createMentionSpanController,
    createMentionSpanLoader,
    createResourceBlockController,
    createResourceBlockLoader,
    createResourceBlockContainer,
    createDatabaseBlockContainer,
    createDatabaseBlockController,
    createDatabaseBlockLoader,
    createValueController,
    createValueContainer,
    createCalloutController,
    createCalloutContainer,
    createMentionPreviewController,
    createMentionPreviewLoader,
    createMentionPreviewContainer,
    createTaskBlockController,
    createTaskBlockLoader,
    createTaskBlockContainer,
    createButtonBlockLoader,
    createTextBlockContainer,
    createTextBlockController,
    createTextBlockLoader,
    createHeadingBlockContainer,
    createHeadingBlockLoader,
    createHeadingBlockController,
    createListItemBlockLoader,
    createListItemBlockContainer,
    createListItemBlockController,
    createRelationOneValueContainer,
    createDatabaseStateProvider,
    createDatabaseFilterLoader,
    createDatabaseFilterContainer,
    createDatabaseFilterController,
    createRelationManyValueContainer,
    createRelationOneValueController,
    createRelationOneValueLoader,
    createRelationManyValueController,
    createRelationManyValueLoader,
} from './view';
import { createEditor } from '@varos/editor-core';
import { createTiptapEditor } from '@varos/editor-tiptap';
import { AppCustomNode } from '@varos/research-editor';
import { Editor } from '@tiptap/core';
import { createBlockContextProvider } from './view/block/context';

export function createReportingRouter(
    config: ReportingRouterInit,
    enhancer?: ReportingRouterEnhancer
) {
    return create({ ...config }, enhancer);
}

function create(
    config: ReportingRouterConfig,
    enhancer?: ReportingRouterEnhancer
): ReportingRouter {
    if (enhancer) {
        return enhancer(create)(config);
    }

    const contextConfig: ReportingContextBaseConfig = {};

    const Context = {};

    // Create all routers needed for the application
    const reportDetailRouter = config.kernel.provider.createRouter({
        state: {
            kind: 'hash',
        },
    });

    // Create a router map to be used throughout the app
    const router = {
        report: reportDetailRouter,
    };

    const infra: ReportingRouterInfra = {
        editor: createEditor<AppCustomNode, Editor | null>({
            adapter: createTiptapEditor({}),
            debounceDelayMs: 500,
        }),
    };

    const containerConfig: ReportingContainerBaseConfig = {
        context: config.context,
        kernel: config.kernel,
        // repository: config.repository,
        UI: config.UI,
        router, // Pass the router map to containers
        repository: config.repository,
        blockContext: createBlockContextProvider(),
        infra,
        state: config.state,
        pubsub: config.pubsub,
    };

    const loaderConfig: ReportingLoaderBaseConfig = {
        repository: config.repository,
    };

    const controllerConfig: ReportingControllerBaseConfig = {
        navigation: {},
        controller: config.controller,
        kernel: config.kernel,
        infra,
    };

    const viewConfig: ReportingViewBaseConfig = {
        infra,
        UI: config.UI,
        Layout: config.Layout,
        router,
        kernel: config.kernel,
        Container: {
            Content: {
                Preview: createMentionPreviewContainer(
                    containerConfig,
                    createMentionPreviewLoader(loaderConfig),
                    createMentionPreviewController(controllerConfig)
                ),
            },
            Property: {
                Filter: createDatabaseFilterContainer(
                    containerConfig,
                    createDatabaseFilterLoader(loaderConfig),
                    createDatabaseFilterController(controllerConfig)
                ),
                Value: createValueContainer(
                    {
                        ...containerConfig,
                        Container: {
                            Relation: {
                                One: createRelationOneValueContainer(
                                    containerConfig,
                                    createRelationOneValueLoader(loaderConfig),
                                    createRelationOneValueController(controllerConfig)
                                ),
                                Many: createRelationManyValueContainer(
                                    containerConfig,
                                    createRelationManyValueLoader(loaderConfig),
                                    createRelationManyValueController(controllerConfig)
                                ),
                            },
                        },
                    },
                    createValueController(controllerConfig)
                ),
            },
            Block: {
                Text: createTextBlockContainer(
                    containerConfig,
                    createTextBlockLoader(loaderConfig),
                    createTextBlockController(controllerConfig)
                ),
                Heading: createHeadingBlockContainer(
                    containerConfig,
                    createHeadingBlockLoader(loaderConfig),
                    createHeadingBlockController(controllerConfig)
                ),
                ListItem: createListItemBlockContainer(
                    containerConfig,
                    createListItemBlockLoader(loaderConfig),
                    createListItemBlockController(controllerConfig)
                ),
                Button: createButtonBlockContainer(
                    containerConfig,
                    createButtonBlockLoader(loaderConfig),
                    createButtonBlockController(controllerConfig)
                ),
                Resource: createResourceBlockContainer(
                    containerConfig,
                    createResourceBlockLoader(loaderConfig),
                    createResourceBlockController(controllerConfig)
                ),
                Callout: createCalloutContainer(
                    containerConfig,
                    createCalloutController(controllerConfig)
                ),
                DatabaseView: createDatabaseBlockContainer(
                    {
                        ...containerConfig,
                        keys: {
                            recordUrlParam: 'record',
                        },
                    },
                    createDatabaseStateProvider(),
                    createDatabaseBlockLoader(loaderConfig),
                    createDatabaseBlockController(controllerConfig)
                ),
                Task: createTaskBlockContainer(
                    containerConfig,
                    createTaskBlockLoader(loaderConfig),
                    createTaskBlockController(controllerConfig)
                ),
            },
            Span: {
                Mention: createMentionSpanContainer(
                    containerConfig,
                    createMentionSpanLoader(loaderConfig),
                    createMentionSpanController(controllerConfig)
                ),
            },
            Toolbar: {
                Item: createToolbarItemContainer(
                    containerConfig,
                    createToolbarItemController(controllerConfig)
                ),
            },
        },
    };
    const ReportOverview = createReportOverviewContainer(
        containerConfig,
        createReportOverviewLoader(loaderConfig),
        createReportOverviewController(controllerConfig),
        createReportOverviewView(viewConfig)
    );

    const ReportDetail = createReportDetailContainer(
        containerConfig,
        createReportDetailLoader(loaderConfig),
        createReportDetailController(controllerConfig),
        createReportDetailView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Source: {
                    List: createSourceListContainer(
                        containerConfig,
                        createSourceListState(),
                        createSourceListLoader(),
                        createSourceListController()
                    ),
                },
            },
        })
    );

    const ReportGenerate = createReportGenerateContainer(
        containerConfig,
        createReportGenerateLoader(loaderConfig),
        createReportGenerateController(controllerConfig),
        createReportGenerateView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Option: createHomeOptionContainer(
                    containerConfig,
                    createHomeOptionController(controllerConfig)
                ),
            },
        })
    );

    const ReportNew = createReportNewContainer(
        containerConfig,
        createReportNewLoader(loaderConfig),
        createReportNewController(controllerConfig),
        createReportNewView(viewConfig)
    );

    const ChapterDetail = createReportChapterContainer(
        containerConfig,
        createReportChapterLoader(loaderConfig),
        createReportChapterController(controllerConfig),
        createReportChapterView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Section: createSectionItemContainer(
                    containerConfig,
                    createReportSectionItemController(controllerConfig)
                ),
            },
        })
    );

    const ContentOverlay = createContentOverlayContainer(
        containerConfig,
        createContentOverlayLoader(loaderConfig),
        createContentOverlayController(controllerConfig),
        createContentOverlayView(viewConfig)
    );

    const ContentDetail = createContentDetailContainer(
        {
            ...containerConfig,
            Container: {
                Report: ReportDetail,
                Chapter: ChapterDetail,
                Document: createDocumentDetailContainer(
                    containerConfig,
                    createDocumentDetailLoader(loaderConfig),
                    createDocumentDetailController(controllerConfig),
                    createDocumentDetailView(viewConfig)
                ),
            },
        },
        createContentDetailLoader(loaderConfig)
    );

    const RecordOverlay = createRecordOverlayContainer(
        {
            ...containerConfig,
            keys: {
                recordUrlParam: 'record',
            },
        },
        createRecordOverlayLoader(loaderConfig),
        createRecordOverlayController(controllerConfig),
        createRecordOverlayView(viewConfig)
    );

    const TaskList = createTaskListContainer(
        containerConfig,
        createTaskListLoader(loaderConfig),
        createTaskListController(controllerConfig),
        createTaskListView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Item: createTaskItemContainer(
                    containerConfig,
                    createTaskItemLoader(loaderConfig),
                    createTaskItemController(controllerConfig)
                ),
            },
        })
    );

    return {
        Pages: {
            Report: {
                Overview: ReportOverview,
                Detail: ReportDetail,
                Generate: ReportGenerate,
                New: ReportNew,
            },
            Chapter: {
                Detail: ChapterDetail,
            },
            Content: {
                Overlay: ContentOverlay,
                Detail: ContentDetail,
            },
            Record: {
                Overlay: RecordOverlay,
            },
            Task: {
                List: TaskList,
            },
        },
        router,
    };
}
